import React, { FC } from 'react';
import { Product } from 'shared/types/product/Product';
import { hasHtmlContent, sanitize } from 'helpers/utils/htmlSanitize';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import ProductsLoadError from 'components/commercetools-ui/atoms/products-load-error';
import ProductTile, { ProductTileLayout } from 'components/commercetools-ui/organisms/product/product-tile';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, hugeDesktop, largeDesktop, minitablet, mobile, tablet } from 'helpers/utils/screensizes';

import { SectionWithProductSliderContent } from './types';
import { useAdditionalCss } from '../helpers/useAdditionalCss';

interface ProductSliderViewProps {
  content: SectionWithProductSliderContent;
  /** Already in display order (see useProductBlock). */
  products: Product[];
  tileLayout: ProductTileLayout;
}

/**
 * "Product IDs" slider layout: no background, left padding only, heading always shown, up to 4.3 tiles
 * per view on huge screens. Presentational only, so the tiles land in the SSR HTML.
 */
export const ProductIDsSliderView: FC<ProductSliderViewProps> = ({ content, products, tileLayout }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);

  const title = content?.title;
  const description = content?.description;
  const hasDescription = hasHtmlContent(description);
  const additional_css = content?.additional_css;
  const additional_css_class_names = content?.additional_css_class_names;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'ProductIDsProductSlider');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div
        id="related-products"
        className={`pl-24 xl:pl-60 2xl:pl-80 md:pl-28 md:pl-40 lg:pl-48 pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}
      >
        <div>
          {/* Blank title: no empty heading (and no 52px of padding under it) — same rule as every
              other product block. */}
          {title && (
            <Typography
              id="related-products-title"
              className={`${hasDescription ? 'pb-16' : 'pb-52 lg:pb-36'} text-32 font-medium text-primary-black xl:text-36 2xl:text-48`}
              as="h3"
            >
              {title}
            </Typography>
          )}
          {hasDescription && (
            <div
              className="pb-52 text-16 font-normal leading-6 text-[#333] md:text-18 lg:pb-36 2xl:text-20 [&>*:first-child]:!mt-0 [&>*:last-child]:!mb-0 [&_p]:my-8"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          )}
          <div className="product-slider-container relative w-full">
            {/* Message replaces the carousel but stays inside the section, so the heading is still shown. */}
            {products.length === 0 ? (
              <ProductsLoadError className="py-24 lg:py-40" />
            ) : (
            <Slider
              className="mb-32"
              slidesPerView={1.2}
              slidesPerGroup={1}
              dots={false}
              arrows={products.length > 1 && (isTabletSize || isDesktopSize || isMiniTabletSize)}
              innerArrows
              autoplay={false}
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '100%', left: 50 }}
              prevButtonStyles={{ top: '100%', left: 0 }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={{
                [mobile]: { slidesPerView: 2.1, spaceBetween: 16 },
                [tablet]: { slidesPerView: 2.1, spaceBetween: 16 },
                [desktop]: { slidesPerView: 3.5, spaceBetween: 16 },
                [hugeDesktop]: { slidesPerView: 4.3, spaceBetween: 16 },
                [largeDesktop]: { slidesPerView: 3.5, spaceBetween: 16 },
              }}
            >
              {products.map((product, index) => (
                <div key={product.productId || index} className="mb-48">
                  <ProductTile product={product} layout={tileLayout} />
                </div>
              ))}
            </Slider>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
